/**
 * 時間前入札 コマ別 自社の入札中の札の件数 (48 コマ分) 取得 hook。
 *
 * ribbon の件数 (AC-BIN-025) 専用。 コマ取引状態 (useMarketProducts) と別 query に
 * しているのは、 件数側が落ちても ribbon の 3 状態表示 (取引中 / 締切間近 / GC済 /
 * 開始前) を巻き添えにしないため。
 *
 * 自社の札の状態は自動更新 (AC-BIN-033、 板情報・約定履歴が対象) の対象外なので、
 * 自社入札の一覧と同じく自動更新の ON/OFF によらず 5 秒ごとに取り直す。 自社の札の
 * 約定は backend の約定取込ジョブが DB に書くため、 市場の約定通知 (NotifyMarket) の
 * 時点では件数がまだ変わっていないことが多く、 通知では取り直さない。
 *
 * 個人設定の業務エリアと JEPX の 9 エリアの共通部分で数える (業務エリアは表示フィルタ
 * 専用で、 対象エリアとの共通部分を画面が条件にする。 conventions.md「業務エリア
 * フィルタの原則」)。 業務エリアで絞るのはこの件数だけで、 自社過不足・自社入札
 * テーブルは全エリアのまま (AC-BIN-103 / AC-BIN-026a)。
 */
import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { listBiddingIntradayActiveOrderCounts } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import { ListBiddingIntradayActiveOrderCountsRequestSchema } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { JEPX_AREA_CODES } from '~/models/area'

const ACTIVE_ORDER_COUNTS_REFETCH_INTERVAL_MS = 5_000

export type ActiveOrderCounts = {
  /**
   * コマ番号 → 自社の入札中の札の件数。 map に無いコマは 0 件。
   * undefined は件数が分かっていない間 (無効化中・業務エリアの未取得・件数の取得中 /
   * 取得失敗)。 取得できていない札を 「無い」 と言い切ると板に残る自社の札を見落とす
   * ので、 未取得を 0 件に潰さない。
   */
  countsByKoma: ReadonlyMap<number, number> | undefined
  error: ConnectError | null
}

export function useActiveOrderCounts(
  targetDate: ProtoDate,
  options: { enabled: boolean; throwOnError: boolean },
): ActiveOrderCounts {
  // 業務エリアを一度も取得できていない間 (取得中・取得失敗) は呼ばない: hook の
  // `selectableAreaCodes` はその間 9 エリア全部を返すので、 そのまま数えると担当外の札が
  // 混じる。 取得済みで再取得だけが失敗した時は取得済みの業務エリアで数え続ける。
  const {
    selectableAreaCodes,
    isBusinessAreasResolved,
    isBusinessAreasLoadFailed,
  } = useBusinessAreaFilter(JEPX_AREA_CODES)
  const businessAreasKnown =
    isBusinessAreasResolved && !isBusinessAreasLoadFailed
  // 共通部分が空 (業務エリアが沖縄だけ) の時は時間前入札の対象エリアが無く、 自社の
  // 入札中の札は無いと確定する。 空の area_codes は backend が拒否するので呼ばない。
  const noTargetAreas = businessAreasKnown && selectableAreaCodes.length === 0

  const { data, error, status } = useQuery(
    listBiddingIntradayActiveOrderCounts,
    create(ListBiddingIntradayActiveOrderCountsRequestSchema, {
      targetDate,
      areaCodes: [...selectableAreaCodes],
    }),
    {
      enabled: options.enabled && businessAreasKnown && !noTargetAreas,
      throwOnError: options.throwOnError,
      refetchInterval: ACTIVE_ORDER_COUNTS_REFETCH_INTERVAL_MS,
    },
  )

  const countsByKoma = useMemo(() => {
    // disabled の query も既存のキャッシュを返すので、業務エリア未取得時は使わない。
    if (!options.enabled || !businessAreasKnown) return undefined
    if (noTargetAreas) return new Map<number, number>()
    if (status !== 'success') return undefined
    return new Map(data.counts.map((c) => [c.timeCode, c.activeOrderCount]))
  }, [options.enabled, businessAreasKnown, noTargetAreas, status, data])

  return { countsByKoma, error }
}
